import Vue from 'vue'
import Message, { SubmitPayload } from '@/api/message'

interface State {
  imageUrl: string
  userId: string
  name: string
  chats: {}
  imagePurchasePoint: number
  videoPurchasePoint: number
  unsentAlreadyReadTimestamps: string[]
  saveImgPoint: string
  saveVideoPoint: string
  enabledInfinite: boolean
}

const defaultState: State = {
  imageUrl: '',
  userId: '',
  name: '',
  chats: {},
  imagePurchasePoint: 0,
  videoPurchasePoint: 0,
  unsentAlreadyReadTimestamps: [],
  saveImgPoint: '',
  saveVideoPoint: '',
  enabledInfinite: false
}

const state: State = Object.assign({}, defaultState)

const mutations = {
  resetState(state: State) {
    const s = defaultState
    Object.keys(s).forEach(key => {
      state[key] = s[key]
    })
  },

  removeChat(state: State, timestamp: number) {
    Vue.delete(state.chats, timestamp)
  },

  setImageUrl(state: State, iconURL: string) {
    state.imageUrl = iconURL
  },

  setUserId(state: State, userId: string) {
    state.userId = userId
  },

  setName(state: State, name: string) {
    state.name = name
  },

  setChat(state, payload: { timestamp: string; chat: any }) {
    state.chats[payload.timestamp] = { ...payload.chat }
  },

  setChats(state: State, chats: {}) {
    state.chats = chats
  },

  setImagePurchasePoint(state: State, point: number) {
    state.imagePurchasePoint = point
  },

  setVideoPurchasePoint(state: State, point: number) {
    state.videoPurchasePoint = point
  },

  pushUnsentAlreadReadTimestamp(state: State, timestamp: string) {
    state.unsentAlreadyReadTimestamps.push(timestamp)
  },

  clearUnsentAlreadReadTimestamp(state: State) {
    state.unsentAlreadyReadTimestamps = []
  },

  setSaveImgPoint(state: State, pt: string) {
    state.saveImgPoint = pt
  },

  setSaveVideoPoint(state: State, pt: string) {
    state.saveVideoPoint = pt
  },

  setEnabledInfinite(state: State, enabledInfinite: boolean) {
    state.enabledInfinite = enabledInfinite
  }
}

const actions = {
  /**
   * チャット一覧を取得
   */
  fetchData({ commit }, payload: any): Promise<any> {
    return Message.fetchData(payload).then(data => {
      if (data.error) return data

      commit('setImagePurchasePoint', data.image_purchase_point)
      commit('setVideoPurchasePoint', data.video_purchase_point)
      commit('setSaveImgPoint', data.save_img_pt)
      commit('setSaveVideoPoint', data.save_video_pt)
      commit('setName', data.name)
      commit('setImageUrl', data.profile_image_url)

      if (data.entries) {
        const keys: string[] = Object.keys(data.entries).filter(elem => elem !== 'is_remainning')
        for (let i = 0; i < keys.length; i++) {
          commit('setChat', {
            timestamp: keys[i],
            chat: data.entries[keys[i]]
          })
        }

        if (data.is_remainning || data.entries.is_remainning) {
          commit('setEnabledInfinite', true)
        }
      }

      return data
    })
  },

  /**
   * submit
   */
  submit({}, payload: SubmitPayload): Promise<any> {
    return Message.submit(payload)
  },

  /**
   * resubmit
   */
  resubmit({}, payload: any): Promise<any> {
    return Message.resubmit(payload)
  },

  /**
   * remove
   */
  remove({ commit }, payload: { id: string; timestamp: string }): Promise<any> {
    return Message.remove(payload).then(data => {
      if (data.error) return data
      commit('removeChat', payload.timestamp)
      return data
    })
  },

  /**
   * 既読通知をサーバに送信
   */
  alreadyRead({}, payload: any): Promise<any> {
    return Message.alreadyRead(payload)
  },

  /**
   * メモを編集
   */
  memo({}, payload: any): Promise<any> {
    return Message.memo(payload)
  },

  /**
   * 添付ファイルの購入
   */
  buy({}, payload: any): Promise<any> {
    return Message.buy(payload)
  }
}

const getters = {
  imageUrl: (state): string => state.imageUrl,
  userId: (state): string => state.userId,
  name: (state): string => state.name,
  chats: (state: State): any => state.chats,
  imagePurchasePoint: (state: State): any => state.imagePurchasePoint,
  videoPurchasePoint: (state: State): any => state.videoPurchasePoint,
  unsentAlreadyReadTimestamps: (state: State): string[] => state.unsentAlreadyReadTimestamps,
  saveImgPoint: (state: State): string => state.saveImgPoint,
  saveVideoPoint: (state: State): string => state.saveVideoPoint,
  enabledInfinite: (state: State): boolean => state.enabledInfinite
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
